/**
 * SendToDeviceModal Component
 * 
 * Displays a QR code for downloading an asset to a mobile device.
 * User scans the QR code with their phone to download the asset directly.
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import './SendToDeviceModal.css';

interface AssetInfo {
  id: string;
  name: string;
  type: string;
  size: number;
  url: string;
  previewUrl?: string;
}

interface SendToDeviceModalProps {
  asset: AssetInfo;
  onClose: () => void;
}

const SendToDeviceModal: React.FC<SendToDeviceModalProps> = ({ asset, onClose }) => {
  const [downloadUrl, setDownloadUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [expiresIn, setExpiresIn] = useState(15 * 60); // 15 minutes in seconds

  useEffect(() => {
    generateDownloadUrl();
  }, [asset]);

  // Countdown timer
  useEffect(() => {
    if (expiresIn <= 0) return;
    
    const timer = setInterval(() => {
      setExpiresIn(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const generateDownloadUrl = async () => {
    setLoading(true);
    setError(null);

    try {
      // The asset.url should already be a signed URL from R2
      let url = asset.url;
      
      // Check if URL is relative (starts with /) and make it absolute
      if (url.startsWith('/')) {
        url = `${window.location.origin}${url}`;
      }
      
      if (url.includes('localhost') || url.includes('127.0.0.1')) {
        setError('Cannot generate QR code for local development URLs. Deploy to production or use a tunnel service.');
        setLoading(false);
        return;
      }

      // Build the mobile download landing page URL with the media URL as a parameter
      const downloadPageUrl = new URL('/download', window.location.origin);
      downloadPageUrl.searchParams.set('url', url);
      downloadPageUrl.searchParams.set('type', asset.type.startsWith('video/') ? 'video' : asset.type.startsWith('image/') ? 'image' : 'file');
      downloadPageUrl.searchParams.set('name', asset.name || 'download');

      setDownloadUrl(downloadPageUrl.toString());
      setLoading(false);
    } catch (err) {
      console.error('[SendToDeviceModal] Failed to generate download URL:', err);
      setError('Failed to generate download link');
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(downloadUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileTypeLabel = (type: string) => {
    if (type.startsWith('image/')) return 'Image';
    if (type.startsWith('video/')) return 'Video';
    if (type.startsWith('audio/')) return 'Audio';
    if (type.includes('pdf')) return 'PDF';
    return 'File';
  };

  const isImage = asset.type.startsWith('image/');
  const isVideo = asset.type.startsWith('video/');

  return ReactDOM.createPortal(
    <div className="send-to-device-overlay" onClick={onClose}>
      <div className="send-to-device-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="header-title">
            <PhoneIphoneIcon className="header-icon" />
            <span>Send to Device</span>
          </div>
          <button className="close-btn" onClick={onClose} title="Close">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {/* Asset Preview */}
          <div className="asset-preview">
            {isImage && (
              <img 
                src={asset.previewUrl || asset.url} 
                alt={asset.name}
                className="preview-image"
              />
            )}
            {isVideo && (
              <video 
                src={asset.url}
                className="preview-video"
                muted
                autoPlay
                loop
                playsInline
              />
            )}
            {!isImage && !isVideo && (
              <div className="preview-placeholder">
                <DownloadIcon className="placeholder-icon" />
                <span className="file-type">{getFileTypeLabel(asset.type)}</span>
              </div>
            )}
          </div>

          {/* Asset Info */}
          <div className="asset-info">
            <div className="asset-name" title={asset.name}>{asset.name}</div>
            <div className="asset-meta">
              <span className="meta-type">{getFileTypeLabel(asset.type)}</span>
              <span className="meta-separator">•</span>
              <span className="meta-size">{formatFileSize(asset.size)}</span>
            </div>
          </div>

          {/* QR Code Section */}
          <div className="qr-section">
            {loading ? (
              <div className="qr-loading">
                <div className="loading-spinner"></div>
                <span>Generating QR code...</span>
              </div>
            ) : error ? (
              <div className="qr-error">
                <span className="error-icon">⚠️</span>
                <span className="error-message">{error}</span>
              </div>
            ) : (
              <>
                <div className="qr-code-container">
                  <QRCodeSVG
                    value={downloadUrl}
                    size={200}
                    level="M"
                    includeMargin={true}
                    bgColor="#ffffff"
                    fgColor="#000000"
                  />
                </div>
                <div className="qr-instructions">
                  <span className="instruction-text">
                    Scan with your phone camera to download
                  </span>
                  {expiresIn > 0 && (
                    <span className="expiry-timer">
                      Link expires in {formatTime(expiresIn)}
                    </span>
                  )}
                  {expiresIn === 0 && (
                    <span className="expiry-expired">
                      Link expired - close and reopen to refresh
                    </span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Copy Link Button */}
          {!loading && !error && downloadUrl && (
            <button 
              className="copy-link-btn"
              onClick={handleCopyLink}
            >
              {copied ? (
                <>
                  <CheckIcon fontSize="small" />
                  Copied!
                </>
              ) : (
                <>
                  <ContentCopyIcon fontSize="small" />
                  Copy Download Link
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SendToDeviceModal;
